import { cva } from "class-variance-authority";
import { createContext, use, useEffect, useRef, useState } from "react";
import { Chip, type ChipProps } from "../chip";
import { Icon, type IconName } from "../icon";

// ============================================================================
// Context
// ============================================================================

type AnchorNavContextValue = {
  activeId: string | undefined;
  orientation: "horizontal" | "vertical";
  onActiveChange: ((id: string) => void) | undefined;
  startScrollLock: (() => void) | undefined;
  scrollRef: React.RefObject<HTMLElement | null> | undefined;
};

const AnchorNavContext = createContext<AnchorNavContextValue>({
  activeId: undefined,
  orientation: "vertical",
  onActiveChange: undefined,
  startScrollLock: undefined,
  scrollRef: undefined,
});

// ============================================================================
// AnchorLink
// ============================================================================

const anchorLinkVariants = cva(
  [
    // Layout
    "relative",
    "inline-flex",
    "items-center",
    "gap-(--spacing-4)",
    "px-(--spacing-6)",
    "rounded-(--radius-sm)",
    // Typography
    "font-(family-name:--font-family-label)",
    "text-(length:--font-size-label-large)",
    "leading-(--font-line-height-16)",
    "tracking-(--font-letter-spacing-default)",
    "whitespace-nowrap",
    "select-none",
    "outline-none",
    "cursor-pointer",
    // Transitions
    "transition-colors",
    "duration-200",
    "ease-in-out",
    // Default text color
    "text-informative",
    // Hover
    "hover:bg-surface-hover",
    // Pressed
    "active:bg-surface-pressed",
    // Focus ring
    "focus-visible:ring-2",
    "focus-visible:ring-button-focus-ring",
  ],
  {
    variants: {
      orientation: {
        // Vertical: fixed 26px height, no extra vertical padding
        vertical: ["h-[26px]"],
        // Horizontal: fixed 26px height, no extra vertical padding
        horizontal: ["h-[26px]"],
      },
      selected: {
        true: [],
        false: [],
      },
      disabled: {
        true: ["opacity-50", "pointer-events-none", "cursor-default"],
      },
    },
    compoundVariants: [
      {
        orientation: "horizontal",
        selected: true,
        className: "font-semibold",
      },
    ],
  },
);

// Wrapper for vertical items — flex row with gap between indicator and link text
const verticalItemWrapperStyles = [
  "flex",
  "items-center",
  "gap-(--spacing-16)",
  "h-[26px]",
  "w-full",
].join(" ");

const selectionIndicatorVariants = cva(
  ["shrink-0", "transition-colors", "duration-200", "ease-in-out"],
  {
    variants: {
      orientation: {
        vertical: [
          "shrink-0",
          "w-(--spacing-2)",
          "h-(--spacing-24)",
          "rounded-tr-(--radius-sm)",
          "rounded-br-(--radius-sm)",
        ],
        horizontal: [
          "absolute",
          "bottom-0",
          "left-[14px]",
          "right-[14px]",
          "h-(--spacing-2)",
          "rounded-tl-(--radius-sm)",
          "rounded-tr-(--radius-sm)",
        ],
      },
      selected: {
        true: "bg-informative",
        false: "bg-surface-container-highest",
      },
    },
  },
);

export interface AnchorLinkProps {
  /**
   * The id of the target section this link points to. The `href` is derived
   * internally as `#${targetId}`. Do not pass an HTML `id` — the targetId
   * is consumed by `AnchorNav` context and is not rendered onto the element.
   *
   * Use for same-page section navigation. For links within body text, use
   * `InlineLink` instead.
   */
  targetId: string;
  /** Optional leading icon from the icon registry. */
  icon?: IconName;
  /** Optional trailing status chip. Do not use for purely decorative information. */
  chip?: {
    label: string | number;
    tone: NonNullable<ChipProps["tone"]>;
    icon?: IconName;
  };
  /** Whether the link is non-interactive. */
  disabled?: boolean;
  children: React.ReactNode;
}

function AnchorLink({
  targetId,
  icon,
  chip,
  disabled = false,
  children,
}: AnchorLinkProps) {
  const { activeId, orientation, onActiveChange, startScrollLock, scrollRef } =
    use(AnchorNavContext);
  const isActive = activeId === targetId;
  const isSelected = !disabled && isActive;

  function handleClick(e: React.MouseEvent<HTMLAnchorElement>) {
    if (disabled) {
      e.preventDefault();
      return;
    }

    // Let the browser handle modifier clicks (Cmd/Ctrl/Shift/Alt) and
    // non-primary buttons (middle-click) natively.
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }

    e.preventDefault();

    const target = document.getElementById(targetId);
    if (target) {
      const prefersReduced = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      const behavior = prefersReduced ? "instant" : "smooth";
      const container = scrollRef?.current;

      if (!prefersReduced) {
        startScrollLock?.();
      }

      if (container) {
        // Scroll the container directly to the target's offset for precise
        // positioning — avoids ancestor scrolling from scrollIntoView.
        container.scrollTo({
          top: target.offsetTop - container.offsetTop,
          behavior: behavior as ScrollBehavior,
        });
      } else {
        target.scrollIntoView({ behavior: behavior as ScrollBehavior });
      }
    }

    window.history.pushState(null, "", `#${targetId}`);
    onActiveChange?.(targetId);
  }

  // Vertical: flex row with indicator as a flow sibling, gap-16 between indicator and link.
  // Grey track shown on all items; blue on the selected item.
  if (orientation === "vertical") {
    return (
      <div className={verticalItemWrapperStyles}>
        <span
          aria-hidden
          className={[
            selectionIndicatorVariants({
              orientation,
              selected: isSelected || (disabled && isActive),
            }),
            disabled && isActive ? "opacity-50" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        />
        <a
          href={`#${targetId}`}
          onClick={handleClick}
          aria-current={isSelected ? "true" : undefined}
          aria-disabled={disabled ? "true" : undefined}
          tabIndex={disabled ? -1 : undefined}
          className={anchorLinkVariants({
            selected: isSelected,
            disabled,
            orientation,
          })}
        >
          {icon && <Icon name={icon} size="xs" tone="inherit" aria-hidden />}
          {children}
          {chip && <Chip {...chip} />}
        </a>
      </div>
    );
  }

  // Horizontal: indicator lives inside the <a> (fixed h-[26px]) so bottom-0
  // is always relative to the link's own bottom edge regardless of icon presence.
  // No grey track — only the blue selected indicator is shown.
  return (
    <a
      href={`#${targetId}`}
      onClick={handleClick}
      aria-current={isSelected ? "true" : undefined}
      aria-disabled={disabled ? "true" : undefined}
      tabIndex={disabled ? -1 : undefined}
      className={anchorLinkVariants({
        selected: isSelected,
        disabled,
        orientation,
      })}
    >
      {icon && <Icon name={icon} size="xs" tone="inherit" aria-hidden />}
      {children}
      {chip && <Chip {...chip} />}
      {(isSelected || (disabled && isActive)) && (
        <span
          aria-hidden
          className={[
            selectionIndicatorVariants({ orientation, selected: true }),
            disabled && isActive ? "opacity-50" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        />
      )}
    </a>
  );
}

// ============================================================================
// AnchorNav
// ============================================================================

export interface AnchorNavProps {
  /** Layout direction. Default: "vertical" (sidebar). Use "horizontal" for a section strip. */
  orientation?: "horizontal" | "vertical";
  /**
   * The `targetId` of the currently active section. Driven by `useAnchorNav`,
   * which keeps this value in sync as the user scrolls.
   */
  activeId?: string;
  /**
   * Called with the `targetId` whenever a link is clicked or `useAnchorNav`
   * selects a new section based on the scroll position.
   */
  onActiveChange?: (id: string) => void;
  /**
   * Called by `AnchorLink` on click to start the scroll lock, which suppresses
   * automatic selection during smooth scrolling.
   * Wired automatically when spreading `useAnchorNav`.
   */
  startScrollLock?: () => void;
  /**
   * Ref to the scrollable container that holds the target sections. When
   * provided, clicking a link scrolls this container directly (via
   * `scrollTo`) instead of using `scrollIntoView`, which avoids scrolling
   * ancestor containers. Wired automatically when spreading `useAnchorNav`.
   */
  scrollRef?: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
}

/**
 * Navigation container for same-page anchor links.
 *
 * **Always pair with `useAnchorNav`** so the active state stays in sync when
 * the user scrolls without clicking a link.
 *
 * Use `orientation="vertical"` (default) for a sidebar table of contents.
 * Use `orientation="horizontal"` for a horizontal section strip.
 *
 * @example
 * const sectionIds = ["overview", "alerts", "history"];
 * const nav = useAnchorNav(sectionIds);
 *
 * <AnchorNav {...nav}>
 *   <AnchorLink targetId="overview">Overview</AnchorLink>
 *   <AnchorLink targetId="alerts">Alerts</AnchorLink>
 *   <AnchorLink targetId="history">History</AnchorLink>
 * </AnchorNav>
 *
 * <div ref={nav.scrollRef} style={{ overflowY: "auto", height: 400 }}>
 *   <section id="overview">...</section>
 *   ...
 * </div>
 */
function AnchorNav({
  orientation = "vertical",
  activeId,
  onActiveChange,
  startScrollLock,
  scrollRef,
  children,
}: AnchorNavProps) {
  return (
    <AnchorNavContext
      value={{
        activeId,
        orientation,
        onActiveChange,
        startScrollLock,
        scrollRef,
      }}
    >
      <nav
        aria-label="On this page"
        className={
          orientation === "vertical"
            ? "flex flex-col items-start gap-(--spacing-8)"
            : "flex flex-row flex-wrap gap-(--spacing-8)"
        }
      >
        {children}
      </nav>
    </AnchorNavContext>
  );
}

// ============================================================================
// useAnchorNav
// ============================================================================

const SCROLL_LOCK_TIMEOUT_MS = 800;

/**
 * Keeps `AnchorNav` in sync with the scroll position.
 *
 * Returns everything needed to wire up both the navigation and the scroll
 * container. Spread the return value onto `<AnchorNav>` and use
 * `scrollRef` on the scroll container.
 *
 * During smooth-scroll animations triggered by clicking an `AnchorLink`,
 * automatic selection is suspended. The clicked section stays selected after
 * scrolling settles, until the user scrolls with a wheel, touch, keyboard, or
 * scrollbar. Manual scrolling selects the last section whose top has reached
 * the top of the scroll viewport, independent of observer callback order.
 *
 * @param ids - Section ids to observe. Must match the `targetId` of each `AnchorLink`.
 *
 * @example
 * const sectionIds = ["overview", "alerts", "history"];
 * const nav = useAnchorNav(sectionIds);
 *
 * <AnchorNav {...nav}>
 *   <AnchorLink targetId="overview">Overview</AnchorLink>
 *   <AnchorLink targetId="alerts">Alerts</AnchorLink>
 *   <AnchorLink targetId="history">History</AnchorLink>
 * </AnchorNav>
 *
 * <div ref={nav.scrollRef} style={{ overflowY: "auto", height: 400 }}>
 *   <section id="overview">...</section>
 *   <section id="alerts">...</section>
 *   <section id="history">...</section>
 * </div>
 */
function useAnchorNav(ids: string[]): {
  scrollRef: React.RefObject<HTMLElement | null>;
  activeId: string;
  onActiveChange: (id: string) => void;
  startScrollLock: () => void;
} {
  const scrollRef = useRef<HTMLElement | null>(null);
  const scrollLockRef = useRef(false);
  const clickedIdRef = useRef<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [activeId, setActiveId] = useState(ids[0]);

  const joinedIds = ids.join(",");

  const endScrollLockRef = useRef<() => void>(() => {});

  useEffect(() => {
    const container = scrollRef.current;
    const idList = joinedIds ? joinedIds.split(",") : [];
    const sections = idList.flatMap((id) => {
      const element = document.getElementById(id);
      return element ? [element] : [];
    });

    function endScrollLock() {
      scrollLockRef.current = false;
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }

    endScrollLockRef.current = endScrollLock;

    function updateActiveSection() {
      if (scrollLockRef.current || clickedIdRef.current !== null) return;

      const viewportTop = container
        ? container.getBoundingClientRect().top + container.clientTop
        : 0;
      // One pixel of tolerance accounts for fractional scroll positions.
      const activationLine = viewportTop + 1;
      let selected = sections[0];
      let selectedTop = Number.NEGATIVE_INFINITY;
      for (const section of sections) {
        const top = section.getBoundingClientRect().top;
        if (top <= activationLine && top >= selectedTop) {
          selected = section;
          selectedTop = top;
        }
      }
      if (selected) setActiveId(selected.id);
    }

    function resumeManualScrolling() {
      clickedIdRef.current = null;
      endScrollLock();
    }

    function handleKeyDown(event: Event) {
      if (!(event instanceof KeyboardEvent)) return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || target.closest("input, textarea, select"))
      ) {
        return;
      }
      if (
        [
          "ArrowUp",
          "ArrowDown",
          "PageUp",
          "PageDown",
          "Home",
          "End",
          " ",
        ].includes(event.key)
      ) {
        resumeManualScrolling();
      }
    }

    const observer = new IntersectionObserver(updateActiveSection, {
      root: container,
      threshold: 0,
    });

    for (const section of sections) observer.observe(section);

    const scrollTarget = container ?? document;
    scrollTarget.addEventListener("scroll", updateActiveSection, {
      passive: true,
    });
    scrollTarget.addEventListener("scrollend", endScrollLock);
    scrollTarget.addEventListener("wheel", resumeManualScrolling, {
      passive: true,
    });
    scrollTarget.addEventListener("touchstart", resumeManualScrolling, {
      passive: true,
    });
    scrollTarget.addEventListener("pointerdown", resumeManualScrolling);
    scrollTarget.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", updateActiveSection);

    if (clickedIdRef.current && !idList.includes(clickedIdRef.current)) {
      clickedIdRef.current = null;
      endScrollLock();
    }

    return () => {
      observer.disconnect();
      scrollTarget.removeEventListener("scroll", updateActiveSection);
      scrollTarget.removeEventListener("scrollend", endScrollLock);
      scrollTarget.removeEventListener("wheel", resumeManualScrolling);
      scrollTarget.removeEventListener("touchstart", resumeManualScrolling);
      scrollTarget.removeEventListener("pointerdown", resumeManualScrolling);
      scrollTarget.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", updateActiveSection);
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [joinedIds]);

  // Stable callback: sets the lock and immediately arms the fallback timer.
  const startScrollLock = useRef(() => {
    scrollLockRef.current = true;
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(
      () => endScrollLockRef.current(),
      SCROLL_LOCK_TIMEOUT_MS,
    );
  }).current;

  function onActiveChange(id: string) {
    clickedIdRef.current = id;
    setActiveId(id);
  }

  return { scrollRef, activeId, onActiveChange, startScrollLock };
}

export { AnchorNav, AnchorLink, useAnchorNav };
